<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'

    import type { IDomain } from 'wdom/domain/interfaces'
    import { Breadcrumb } from 'breadcrumb'

    import type { DomainHandler, DomainStatus } from './domain'

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const domainIdna = getContext('domainIdna') as Readable<string>
    const Domain = getContext('Domain') as DomainHandler

    // const status = sderived(Domain.status, ($status) => $status)
    let status: DomainStatus = $state(null)

    let domain: IDomain = $derived(status == 'ready' ? Domain.domain : ({} as IDomain))

    let nameservers: string[] = $state([])
    let newNameserver = $state('')
    let originalNameservers: string[]
    let saving = $state(false)

    let canImportFromZone = $derived(status == 'ready' ? Domain.zoneDomainRelation.zone_exists && Domain.zoneDomainRelation.zone_published : false)

    function load() {
        originalNameservers = [...domain.nameservers]
        originalNameservers.sort((a, b) => a.localeCompare(b))
        if (route.result.querystring.original['import-from-zone'] == '1' && canImportFromZone) {
            importFromZone()
        } else {
            nameservers = [...originalNameservers]
        }
    }
    function importFromZone() {
        if (canImportFromZone) {
            nameservers = []
            for (const ns of Domain.zoneDomainRelation.zone_ns) {
                nameservers.push(toUnicode(ns))
            }
            nameservers.sort((a, b) => a.localeCompare(b))
        }
    }

    unsubs.push(
        Domain.status.subscribe(($v) => {
            setTimeout(() => {
                status = $v
                if ($v == 'ready') load()
            }, 0)
        }),
    )

    let nameserversOk: boolean[] = $state([])
    let allOk = $state(false)
    $effect(() => {
        let localNsOk: boolean[] = []
        let localAllOk = true
        for (const ns of nameservers) {
            const ok = ns != null && ns != '' && !!ns.match(/^[^\. ]+(\.[^\. ]+)+$/)
            localNsOk.push(ok)
            localAllOk &&= ok
        }
        nameserversOk = localNsOk
        allOk = localAllOk
    })
    let newOk = $derived(!!newNameserver.match(/^[^\. ]+(\.[^\. ]+)+$/))
    let changed = $derived(!isEqual(nameservers, domain.nameservers))

    let nameserversInError: Array<string> = $state([])

    const crumbs = $derived([
        { txt: $_('Domaines'), url: spath('/domains') },
        {
            txt: status == 'ready' ? Domain.unicode : status == 'loading' ? '…' : iconExclamationTriangle,
            html: status != 'ready' && status != 'loading',
            url: status == 'ready' ? spath(`/domain/${$domainIdna}`) : undefined,
        },
        { txt: $_('Serveurs DNS') },
    ])

    function cleanup() {
        const set: Set<string> = new Set()
        for (const ns of nameservers) {
            set.add(ns.match(/(^|\.)xn--/) ? toUnicode(ns) : ns)
        }
        nameservers = [...set.values()]
        nameservers.sort((a, b) => a.localeCompare(b))
    }
    function remove(ns: string) {
        const set = new Set(nameservers)
        set.delete(ns)
        nameservers = [...set.values()]
        nameservers.sort((a, b) => a.localeCompare(b))
    }
    function add() {
        if (!newOk) return
        const set = new Set(nameservers)
        set.add(newNameserver.match(/(^|\.)xn--/) ? toUnicode(newNameserver) : newNameserver)
        nameservers = [...set.values()]
        nameservers.sort((a, b) => a.localeCompare(b))
        newNameserver = ''
        const inp = document.getElementById('ns-add-input')
        if (inp) inp.focus()
    }
    async function submit() {
        if (!allOk || newNameserver != '' || !changed) return

        saving = true
        let res: SimpleFetchResult
        try {
            res = await simpleFetch('POST', `/json/domain/1/${$domainIdna}/nameservers`, {
                nameservers: [...nameservers],
            })
            if (res.status == 202 || res.status == 200) {
                sgoto(`/domain/${$domainIdna}`)
                return
            }
        } catch (e: any) {
            res = e
        }

        saving = false
        notify({
            title: $_('Serveurs DNS'),
            text: $_('L’information n’a pas pu être sauvegardée.'),
            type: 'danger',
        })
        nameserversInError = []
        if (res && res.data && res.data.ecode === 'INVALID') {
            nameserversInError = [...res.data.invalid_nameservers, ...res.data.missing_nameservers]
        }
        nameserversInError.sort((a, b) => a.localeCompare(b))
    }
</script>

<Breadcrumb {crumbs} />

{#if status == 'not-found' || status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else}
    <div id="container-domain" class="status-{status}">
        <div class="title-domain">
            <div>
                <h1>
                    {#if status == 'loading'}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Loading…')}
                    {:else}
                        <Icon inline icon="fa:globe" /> {Domain.unicode}
                    {/if}
                </h1>
            </div>
        </div>
    </div>

    {#if domain.status == 'ready'}
        <div class="card">
            <div class="card-title text-primary">{$_('Serveurs DNS')}</div>
            <div class="card-body">
                <form novalidate class="d-grid gap-2">
                    {#each nameservers as ns, i}
                        <div class="row clearfix">
                            <div class="col-lg-10 col-md-12 offset-lg-1">
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control"
                                        required={true}
                                        bind:value={nameservers[i]}
                                        onkeypress={(e) => {
                                            if (e.key === 'Enter') cleanup()
                                        }}
                                        onblur={() => cleanup()}
                                        disabled={saving}
                                        class:is-invalid={!nameserversOk[i]}
                                    />
                                    <button class="btn btn-danger" type="button" onclick={() => remove(ns)} disabled={saving}><Icon inline icon="fa:trash-o" /></button>
                                </div>
                            </div>
                        </div>
                    {/each}
                    <div class="row clearfix">
                        <div class="col-lg-10 col-md-12 offset-lg-1">
                            <div class="input-group">
                                <input
                                    type="text"
                                    class="form-control"
                                    id="ns-add-input"
                                    bind:value={newNameserver}
                                    onblur={() => add()}
                                    onkeypress={(e) => {
                                        if (e.key === 'Enter') add()
                                    }}
                                    disabled={saving}
                                    class:is-invalid={newNameserver != '' && !newOk}
                                />
                                <button class="btn btn-primary" type="button" disabled={newNameserver == '' || !newOk || saving} onclick={add}><Icon inline icon="fa:plus" /></button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            {#if nameserversInError.length}
                <div class="card-body">
                    <p>{$_('The following nameservers are invalid or missing:')}</p>
                    <ul>
                        {#each nameserversInError as ns}
                            <li>{toUnicode(ns)}</li>
                        {/each}
                    </ul>
                </div>
            {/if}
            <div class="card-footer text-end text-bg-light">
                <button type="button" class="btn btn-outline-secondary" disabled={saving} onclick={() => importFromZone()}>{$_('Import nameservers from zone')}</button>
                {#if status == 'ready'}
                    <a use:sanchor href={spath(`/domain/${$domainIdna}`)} class="btn btn-outline-secondary">{$_('Cancel')}</a>
                {/if}
                <button type="button" class="btn btn-primary" onclick={() => submit()} disabled={newNameserver != '' || !changed || !allOk || saving}>{$_('Save')}</button>
            </div>
        </div>
    {/if}
{/if}
